import { ChangeDetectionStrategy, Component, computed, inject, input, output, Signal, signal, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { UntypedFormControl } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';

import { MultiFormSelectOptions, ShFormMultiSelectModule, ShIconModule } from '@stylehaven/components';

import { SavedQuery } from '../../../entities/saved-queries/saved-query.entity';

// Unused now that ticket 06 replaced the tabs with a flat select — kept for the commented-out
// tabs/list markup below, in case that shape comes back.
// import { SavedQueryGroups, splitSavedQueriesByShared } from '../../helpers/saved-query-groups.helper';
// export type SavedQueriesPanelTab = 'mine' | 'shared';

/**
 * The "Bewaarde zoekopdrachten" block in the filter panel head.
 *
 * Revised in ticket 04: rather than an always-visible inline section, a single trigger shows the
 * currently active query's name (or the panel title when none is active) and opens a popup instead of
 * showing the list inline.
 *
 * Revised again in ticket 06: the popup lists every query flat — via the same `sh-form-multi-select`
 * the load-saved-query dialog already uses — with a "(gedeeld)" suffix on shared ones, replacing the
 * mine/shared tabs from ticket 02.
 *
 * Ticket 07 added "Bijwerken": once the screen has drifted from the active query's stored `Query`, a
 * second button next to "Bewaar zoekopdracht" (same styling) offers to overwrite it in place.
 */
@Component({
    standalone: true,
    selector: 'digihaven-saved-queries-panel',
    templateUrl: './saved-queries-panel.component.html',
    styleUrls: ['./saved-queries-panel.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [CommonModule, TranslateModule, ShIconModule, ShFormMultiSelectModule, RouterLink]
})
export class SavedQueriesPanelComponent {
    private readonly translateService = inject(TranslateService);

    /** `null` while the store has not resolved yet, so the empty-state hint never flashes before it does. */
    readonly queries = input<SavedQuery[] | null>(null);

    /** True once the fetch behind {@link queries} has failed; takes precedence over the select. */
    readonly hasError = input<boolean>(false);

    /** The organisation-wide total, which {@link queries} alone cannot give once it is capped server-side. */
    readonly totalCount = input<number>(0);

    /** `Id` of the query currently loaded into the search bar, if any — drives the trigger's label. */
    readonly activeQueryId = input<string | null>(null);

    /** Whether the search on screen has anything worth saving — mirrors the search bar's own "save" action. */
    readonly canSaveQuery = input<boolean>(false);

    /**
     * Whether the active query's stored `Query` structurally differs from what's on screen — the container
     * compares structured queries, not raw Lucene text (ticket 07): re-serialising the same facets/search
     * term can format differently than what was originally saved, so a raw-string compare drifts false
     * even with nothing actually changed.
     */
    readonly canUpdateQuery = input<boolean>(false);

    readonly loadQuery = output<SavedQuery>();

    /** The container owns the current query and the save dialog (ticket 05) — this is just the ask to open it. */
    readonly saveQuery = output<void>();

    /** Overwrites the active query's stored `Query` with what's on screen (ticket 07) — container owns the confirm + call. */
    readonly updateQuery = output<SavedQuery>();

    /** Whether the popup is open. Previously meant "section expanded" for the always-visible header. */
    readonly expanded: WritableSignal<boolean> = signal(false);

    // Superseded by ticket 06's flat select — kept in case the tabs come back (see the html/scss files):
    //
    // readonly activeTab: WritableSignal<SavedQueriesPanelTab> = signal<SavedQueriesPanelTab>('mine');
    // private readonly groups: Signal<SavedQueryGroups> = computed(() => splitSavedQueriesByShared(this.queries() ?? []));
    // readonly mine: Signal<SavedQuery[]> = computed(() => this.groups().mine);
    // readonly shared: Signal<SavedQuery[]> = computed(() => this.groups().shared);
    // readonly visible: Signal<SavedQuery[]> = computed(() => (this.activeTab() === 'mine' ? this.mine() : this.shared()));
    // setTab(tab: SavedQueriesPanelTab): void {
    //     this.activeTab.set(tab);
    // }

    /** `null` once nothing is active, or while {@link queries} has not resolved yet. */
    readonly activeQuery: Signal<SavedQuery | null> = computed(
        () => (this.queries() ?? []).find(query => query.Id === this.activeQueryId()) ?? null
    );

    /** Bound to `sh-form-multi-select` — the same control-passing shape `LoadSavedQueryDialogComponent` uses. */
    readonly querySelectControl: UntypedFormControl = new UntypedFormControl([]);

    /** One flat, alphabetically-untouched list — shared queries get a "(gedeeld)" suffix instead of a tab. */
    readonly selectOptions: Signal<MultiFormSelectOptions<string>> = computed(() => ({
        isLiveData: false,
        options: (this.queries() ?? []).map(query => ({
            label:
                query.Groups.length > 0
                    ? `${query.Name} ${this.translateService.instant('components.saved-queries.panel.shared-suffix')}`
                    : query.Name,
            value: query.Id
        }))
    }));

    constructor() {
        this.querySelectControl.valueChanges.pipe(takeUntilDestroyed()).subscribe((values: string[] | null) => {
            const query: SavedQuery | undefined = (this.queries() ?? []).find(candidate => candidate.Id === values?.[0]);

            if (query) {
                this.selectQuery(query);
            }
        });
    }

    toggleExpanded(): void {
        this.expanded.update(expanded => !expanded);
    }

    /** Loading a query from the popup also closes it — the search bar it just updated is what the user came to see. */
    selectQuery(query: SavedQuery): void {
        this.loadQuery.emit(query);
        this.expanded.set(false);
    }

    /** Closes the popup before handing off to the container — the save dialog it opens is the next thing to look at. */
    requestSave(): void {
        this.saveQuery.emit();
        this.expanded.set(false);
    }

    /** Closes the popup before handing off to the container — the confirm dialog it opens is the next thing to look at. */
    requestUpdate(): void {
        const active: SavedQuery | null = this.activeQuery();

        if (active?.Context.IsEditable) {
            this.updateQuery.emit(active);
            this.expanded.set(false);
        }
    }
}
